import { useState } from 'react';

export default function Hero() {
  const [showDemo, setShowDemo] = useState(false);

  const scrollToFeatures = () => {
    const element = document.getElementById('features');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section className="relative pt-[140px] pb-[120px] overflow-hidden bg-white dark:bg-[#0A0E1A]">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-radial from-[#6366F1]/10 dark:from-[#1E1B4B]/40 via-transparent to-transparent"></div>

      <div className="relative max-w-[900px] mx-auto px-6">
        {/* Top Badge */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2 h-10 px-5 bg-transparent border border-[#6366F1] rounded-full">
            <span className="text-[#818CF8] text-base">✦</span>
            <span className="text-[#818CF8] text-sm font-medium">AI-Powered · Used across 50+ countries</span>
          </div>
        </div>

        {/* Main Headline */}
        <h1 className="text-center mb-6">
          <div className="text-[72px] md:text-[72px] text-[42px] font-extrabold leading-[1.1] tracking-tight">
            <span className="text-gray-900 dark:text-white">The </span>
            <span className="bg-gradient-to-r from-gray-900 dark:from-white via-[#818CF8] to-[#6366F1] bg-clip-text text-transparent">Operating System</span>
          </div>
          <div className="text-[72px] md:text-[72px] text-[42px] font-extrabold leading-[1.1] tracking-tight text-gray-900 dark:text-white">
            for Your Job Search.
          </div>
        </h1>

        {/* Subheadline */}
        <p className="text-center text-gray-500 dark:text-[#94A3B8] text-[20px] leading-[1.6] max-w-[640px] mx-auto mb-10">
          Track every application. Autofill every ATS. Tailor your resume with AI.
          JobOS is the complete platform for serious job seekers — on every job board,
          in every country, at every stage of your search.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
          <a
            href="https://app.jobos.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="h-14 px-10 bg-[#6366F1] text-white text-lg font-semibold rounded-full transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(99,102,241,0.4)] flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer w-full sm:w-auto"
          >
            Get Started Free
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
          <button
            onClick={() => setShowDemo(true)}
            className="h-14 px-10 bg-transparent border border-gray-300 dark:border-[#2D3148] text-gray-800 dark:text-[#F8FAFC] text-lg font-semibold rounded-full transition-all duration-200 hover:border-[#6366F1] flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer w-full sm:w-auto"
          >
            <i className="ri-play-circle-line text-xl"></i>
            Watch Demo
          </button>
        </div>

        {/* Social Proof Line */}
        <p className="text-center text-gray-400 dark:text-[#64748B] text-[13px] mb-10">
          Free to start · No credit card · Works on LinkedIn, Indeed, Rozee, Naukri, Glassdoor &amp; 20+ more
        </p>

        {/* Traction Stats Bar */}
        <div className="flex flex-wrap items-center justify-center gap-8 mb-16 py-6 border-t border-b border-gray-200 dark:border-[#2D3148]">
          {[
            { value: '4,800+', label: 'Active Job Seekers' },
            { value: '60K+', label: 'Active Jobs Listed' },
            { value: '340K+', label: 'Applications Tracked' },
            { value: '50+', label: 'Countries' },
            { value: '9+', label: 'ATS Platforms Supported' },
            { value: '14%', label: 'Avg. Response Rate Lift' },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div className="text-2xl font-extrabold text-gray-900 dark:text-white">{stat.value}</div>
              <div className="text-gray-400 dark:text-[#475569] text-xs mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Hero Visual */}
        <div className="relative max-w-[1100px] mx-auto">
          <div className="absolute inset-0 bg-gradient-to-t from-[#6366F1]/20 via-transparent to-transparent blur-3xl"></div>
          <div className="relative rounded-xl border border-gray-200 dark:border-[#2D3148] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.15)] dark:shadow-[0_24px_64px_rgba(0,0,0,0.6)]">
            <img
              src="https://public.readdy.ai/ai/img_res/e0d1189592989427b9d1f07b72315dea.jpg"
              alt="JobOS dashboard showing job application tracker with pipeline stages, analytics, and ATS integration"
              className="w-full h-auto object-cover object-top"
              loading="eager"
            />
          </div>

          {/* Floating Chrome Extension Popup */}
          <div className="absolute -right-4 top-1/4 hidden lg:block">
            <div className="w-[280px] bg-white dark:bg-[#141828] border border-gray-200 dark:border-[#2D3148] rounded-lg shadow-[0_12px_32px_rgba(0,0,0,0.15)] dark:shadow-[0_12px_32px_rgba(0,0,0,0.5)] p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#6366F1]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#818CF8] text-lg">✦</span>
                </div>
                <div>
                  <div className="text-gray-900 dark:text-[#F8FAFC] text-sm font-semibold mb-1">Extract · ATS Detected</div>
                  <div className="text-gray-500 dark:text-[#94A3B8] text-xs font-mono">Workday</div>
                  <button className="mt-3 h-8 px-4 bg-[#6366F1] text-white text-xs font-semibold rounded-md hover:bg-[#4F46E5] transition-colors cursor-pointer whitespace-nowrap">
                    Autofill Form
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Modal */}
      {showDemo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
          onClick={() => setShowDemo(false)}
        >
          <div
            className="relative w-full max-w-[900px] bg-white dark:bg-[#0A0E1A] border border-gray-200 dark:border-[#2D3148] rounded-2xl overflow-hidden shadow-[0_32px_80px_rgba(0,0,0,0.8)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-[#2D3148]">
              <div className="flex items-center gap-2">
                <span className="text-[#818CF8] text-base">✦</span>
                <span className="text-gray-900 dark:text-white font-semibold">JobOS — Product Demo</span>
              </div>
              <button
                onClick={() => setShowDemo(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-900 dark:text-[#475569] dark:hover:text-white transition-colors cursor-pointer rounded-full hover:bg-gray-100 dark:hover:bg-[#2D3148]"
                aria-label="Close demo"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1&rel=0&modestbranding=1"
                title="JobOS Product Demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-[#2D3148]">
              <span className="text-gray-400 dark:text-[#475569] text-sm">Full walkthrough · 4 min</span>
              <a
                href="https://app.jobos.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="h-9 px-6 bg-[#6366F1] text-white text-sm font-semibold rounded-full hover:bg-[#4F46E5] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                Try it Free
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
